import { BookOpen, ExternalLink, Check } from 'lucide-react';
import { useInView } from '@/hooks/useInView';
import cover from '@/assets/img2nhs-book-cover.png.asset.json';

export const BOOK_URL = 'https://img2nhs.com/ebook';

const facts = [
  { value: '9', label: 'Parts' },
  { value: '40', label: 'Chapters' },
  { value: '5', label: 'Country guides' },
  { value: '2026/27', label: 'Edition' },
];

const covers = [
  'GMC registration & document bundle',
  'PLAB 1, PLAB 2 & MRCP decisions',
  'Visa routes, sponsorship & family',
  'First NHS job, CV & interviews',
  'NHS pay, banding & real costs',
  'Revalidation, appraisal & CPD',
];

export const Book = () => {
  const { ref, inView } = useInView(0.1);

  return (
    <section id="book" className="py-20 sm:py-24 md:py-28" ref={ref}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-10 sm:mb-12 md:mb-14 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <BookOpen size={14} className="text-primary" />
            <span className="text-[10px] sm:text-xs font-bold text-primary uppercase tracking-[0.2em]">Authored Publication</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            My Book
          </h2>
          <div className="w-12 h-1 bg-accent rounded-full mx-auto" />
        </div>

        <div className="grid md:grid-cols-5 gap-10 md:gap-14 items-center">
          <div className="md:col-span-2 flex justify-center">
            <a
              href={BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get the ebook on IMG2NHS"
              className="block w-full max-w-[300px] rounded-lg overflow-hidden shadow-2xl ring-1 ring-accent/40 transition-transform duration-500 hover:-translate-y-2 hover:rotate-[-1deg]"
              onContextMenu={(e) => e.preventDefault()}
            >
              <img
                src={cover.url}
                alt="Cover of From International Doctor to NHS Clinician, 2026/27 Edition, by Dr Yesh Jitesh"
                className="w-full h-auto select-none pointer-events-none"
                draggable={false}
                loading="lazy"
              />
            </a>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs font-bold text-accent uppercase tracking-[0.2em] mb-3">IMG2NHS · 2026/27 Edition</p>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              From International Doctor to NHS Clinician
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mb-6 leading-relaxed">
              The complete, honest pathway guide for international medical graduates — GMC registration, PLAB, visas, NHS jobs, pay and revalidation, mapped end to end. Written and clinically reviewed by Dr Yesh Jitesh, NHS clinician and founder of IMG2NHS, with dedicated chapters for doctors from India, Pakistan, Nigeria, Egypt and the Philippines.
            </p>

            <div className="grid grid-cols-4 gap-2 sm:gap-3 mb-6">
              {facts.map((f) => (
                <div key={f.label} className="text-center rounded-xl border border-border/60 bg-card/70 backdrop-blur-sm py-3 px-1">
                  <div className="text-base sm:text-xl font-bold text-primary" style={{ fontFamily: "'Playfair Display', serif" }}>{f.value}</div>
                  <div className="text-[10px] sm:text-xs text-muted-foreground">{f.label}</div>
                </div>
              ))}
            </div>

            <ul className="grid sm:grid-cols-2 gap-2 mb-8">
              {covers.map((c) => (
                <li key={c} className="flex items-start gap-2 text-sm text-foreground/90">
                  <Check size={16} className="text-accent mt-0.5 shrink-0" />
                  {c}
                </li>
              ))}
            </ul>

            <a
              href={BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold shadow-lg transition-all hover:bg-primary/90 hover:gap-3"
            >
              Get the Ebook <ExternalLink size={16} />
            </a>
            <p className="text-xs text-muted-foreground mt-3">Available exclusively at img2nhs.com/ebook</p>
          </div>
        </div>
      </div>
    </section>
  );
};
